<!--
  @fileoverview Compact Card Component - Card-style Message Layout (Vue 3)

  Renders compact card-style messages with optional image, title, subtext,
  and action buttons. Used for displaying structured content like product
  cards, FAQ items, or any content that benefits from a card layout.

  @component MessageCompact
  @displayName Compact Card Message

  Features:
  - Card layout with optional image thumbnail
  - Title and subtext content
  - Read more/less functionality for long content
  - Action buttons (link or postback)
  - Popup window support for external links
  - UI Beta mode with different styling

  @example
  <MessageCompact
    :message="cardMessage"
    :button-outline="'primary'"
    :should-disable-if-in-livechat="false"
    :background-style="{ backgroundColor: '#fff' }"
    :title-style="{ color: '#333' }"
    @sendPostback="handleButtonClick"
    @setLightBoxImage="openImageLightbox"
  />

  @vue3migration
  - Migrated to script setup syntax
  - Replaced mapGetters with useMainStore()
  - Replaced this.$store.getters.settings with store.settings
  - Replaced this.$set() with direct assignment (Vue 3 reactivity)
  - Added proper TypeScript-style prop definitions
-->
<template>
  <div class="p-[10px]">
    <el-card
      v-for="(item, index) in message.data.content"
      :key="index"
      class="compact-container mb-[10px]"
    >
      <div class="compact-content">
        <img
          v-if="itemImage(item)"
          :src="itemImage(item)"
          class="image cursor-pointer bg-cover"
          @click="emit('setLightBoxImage', item.image)"
        />
        <div class="content p-2">
          <span class="kr-title break-words" v-text="item.text" />

          <div class="my-1 mx-0">
            <div
              v-if="
                !shouldShowFullContent[index] &&
                readMoreEnabled &&
                item.subtext?.length > readMoreCharacterCount
              "
            >
              <span
                class="subtext break-words whitespace-pre-line"
                v-html="truncateText(item.subtext)"
              ></span>
              <div class="keyreply-read-more" @click="setShowFullContent(index)">Read More</div>
            </div>
            <span
              v-else
              class="subtext break-words whitespace-pre-line"
              v-html="item.subtext"
            ></span>
          </div>
          <div
            v-for="(button, buttonIndex) in item.buttons"
            :key="buttonIndex"
            class="compact-button text-center block"
          >
            <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
            <el-button
              v-if="
                !((button.type && button.type === 'website') || button.url) ||
                isDisabledButton(button) ||
                button.popupWindow?.enabled
              "
              class="kr-button w-full mb-2"
              :class="{ 'blue-button': buttonOutline === 'primary' }"
              v-text="button.text"
              target="_blank"
              :type="buttonOutline"
              :plain="true"
              :disabled="isDisabledButton(button)"
              @click.stop.prevent="onClick($event, button)"
            />
            <a v-else :href="validUrl(button.url)" :target="button.popup ?? '_blank'">
              <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
              <el-button
                class="kr-button w-full mb-2"
                :class="{ 'blue-button': buttonOutline === 'primary' }"
                v-text="button.text"
                target="_blank"
                :type="buttonOutline"
                :plain="true"
                @click="onClick($event, button)"
              />
            </a>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Compact
 * @description Compact card message component for structured content (Vue 3 Composition API)
 */
import { ref, computed } from 'vue'
import _ from 'lodash'
import { useMainStore } from '@/stores/main'
import isDisabledButtonHelper from '@/helpers/isDisabledButton'
import validUrlHelper from '@/helpers/validUrl'

/**
 * Component props with proper types
 */
const props = defineProps({
  /**
   * Message object containing card content data
   */
  message: {
    type: Object,
    required: true,
  },
  /**
   * Button outline style/type
   */
  buttonOutline: {
    type: String,
    default: '',
  },
  /**
   * Whether to disable buttons when in live chat mode
   */
  shouldDisableIfInLivechat: {
    type: Boolean,
    default: false,
  },
  /**
   * Background style object for the card
   */
  backgroundStyle: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Title style object
   */
  titleStyle: {
    type: Object,
    default: () => ({}),
  },
})

/**
 * Component emits
 * @event sendPostback - Emits button click to parent for processing
 * @event setLightBoxImage - Emits image URL for lightbox display
 */
const emit = defineEmits(['sendPostback', 'setLightBoxImage'])

/**
 * Pinia store instance
 */
const store = useMainStore()

/**
 * Reactive state for tracking which content items are expanded
 */
const shouldShowFullContent = ref({})

/**
 * Check if UI Beta mode is enabled from store
 */
const isUiBetaEnabled = computed(() => store.isUiBetaEnabled)

/**
 * Access webchat settings from Pinia store
 */
const settings = computed(() => store.settings)

/**
 * Get read more character count from settings
 * @returns {number} Character count threshold for truncation
 */
const readMoreCharacterCount = computed(() => {
  return settings.value.readMoreConfigure.readMoreCharacterCount
})

/**
 * Check if read more feature is enabled
 * @returns {boolean} True if read more is enabled
 */
const readMoreEnabled = computed(() => {
  return settings.value.readMoreConfigure.enabled
})

/**
 * Validate and format URL
 * @param {string} url - URL to validate
 * @returns {string} Validated URL
 */
function validUrl(url) {
  return validUrlHelper(url)
}

/**
 * Check if button should be disabled
 * @param {Object} button - Button object to check
 * @returns {boolean} True if button should be disabled
 */
function isDisabledButton(button) {
  // Create a context object that mimics the component instance
  // for backward compatibility with the helper function
  const context = {
    message: props.message,
    settings: settings.value,
    shouldDisableIfInLivechat: props.shouldDisableIfInLivechat,
    $store: {
      getters: {
        settings: settings.value,
        messages: store.messages,
        isSendingPostback: store.isSendingPostback,
      },
    },
  }
  return isDisabledButtonHelper(context, button)
}

/**
 * Get image URL from item
 * @param {Object} item - Content item object
 * @returns {string|undefined} Image URL or undefined
 */
function itemImage(item) {
  const image = item.image || _.get(item, 'images[0]')
  return image
}

/**
 * Handle button click
 * @param {Event} $event - Click event
 * @param {Object} button - Button object with action data
 */
function onClick($event, button) {
  // Handle popup window with custom dimensions
  if (button.url && button.popupWindow?.enabled) {
    $event.preventDefault()
    const width = button.popupWindow.width || 500
    const height = button.popupWindow.height || 600
    const windowFeatures = `width=${width},height=${height}`
    window.open(validUrl(button.url), '_blank', windowFeatures)
  }
  const _button = {
    ...button,
    $event,
  }
  emit('sendPostback', _button)
}

/**
 * Truncate text for read more functionality
 * @param {string} text - Text to truncate
 * @returns {string} Truncated text with ellipsis
 */
function truncateText(text) {
  if (!text) return ''
  return text.slice(0, readMoreCharacterCount.value) + '...'
}

/**
 * Set content item to show full content
 * @param {number} index - Index of content item to expand
 */
function setShowFullContent(index) {
  shouldShowFullContent.value[index] = true
}
</script>

<style lang="scss" scoped>
.compact-container :deep(.el-card__body) {
  padding: 0px;
}

.image {
  max-width: 200px;
  width: 40%;
  height: auto;
}

.keyreply-read-more {
  color: var(--keyreply-agent-text-color);
  font-weight: 600;
  font-style: italic;
  cursor: pointer;
}

.ui-beta {
  .compact-container {
    border-radius: 16px;
    overflow: hidden;

    .compact-content {
      display: flex;
    }
    .compact-button {
      text-align: left;
      width: 100%;
      margin-top: 20px;

      .kr-button {
        padding: 8px;
        white-space: normal !important;
        line-height: 18px;
      }
    }
  }

  .image {
    max-width: 200px;
    width: 40%;
  }
  .content {
    flex: 1;
    background: var(--keyreply-user-main-bg-color);

    .kr-title {
      color: var(--keyreply-user-main-title-color);
      font-size: 16px;
      font-weight: 600;
    }

    .subtext {
      font-size: 16px;
      color: #333;
    }
  }
  .kr-button {
    padding: 5px;
    border-radius: 8px;

    &.blue-button {
      border: 1px solid #4e6cce;
      color: #4e6cce;
      background: #fff;

      &:hover {
        color: #fff;
        background: #4e6cce;
      }
    }
  }
}
</style>
